/* Cart drawer (Hilaine design): progress bar, product offers slider, item count, theme editor preview.
   Dawn re-renders the drawer's HTML after every cart change; these custom elements rebuild themselves
   each time they are put back on the page (connectedCallback). */

/* Money in the shopper's currency.
   Liquid renders 123456789 cents with the shopper's own money format (e.g. "$1,234,567.89" or
   "1.234.568 Ft"). Reading that sample tells us the symbol position, separators and decimals. */
window.cdhMoney = window.cdhMoney || (() => {
  const cache = {};
  const parse = (sample) => {
    const text = String(sample || '$1,234,567.89').trim();
    const first = text.search(/\d/);
    let last = -1;
    for (let i = text.length - 1; i >= 0; i -= 1) {
      if (/\d/.test(text[i])) {
        last = i;
        break;
      }
    }
    if (first < 0) return { prefix: '$', suffix: '', thousands: ',', decimal: '.', decimals: 2 };
    const core = text.slice(first, last + 1);
    const decimalMatch = core.match(/([.,])(\d{2})$/);
    const hasDecimals = !!decimalMatch && core.endsWith('89');
    const integerPart = hasDecimals ? core.slice(0, -3) : core;
    const thousandsMatch = integerPart.match(/\D/);
    return {
      prefix: text.slice(0, first),
      suffix: text.slice(last + 1),
      thousands: thousandsMatch ? thousandsMatch[0] : '',
      decimal: hasDecimals ? decimalMatch[1] : '',
      decimals: hasDecimals ? 2 : 0,
    };
  };
  return {
    format(cents, sample) {
      const f = cache[sample] || (cache[sample] = parse(sample));
      const value = Math.max(0, cents) / 100;
      const [whole, fraction] = value.toFixed(f.decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, f.thousands);
      // Whole amounts drop ".00" to save space, like the product page buttons.
      const showFraction = fraction && Number(fraction) !== 0;
      return `${f.prefix}${grouped}${showFraction ? f.decimal + fraction : ''}${f.suffix}`;
    },
    // Converted amounts are rounded to what the currency can show (whole units when it has no decimals).
    convert(storeCents, sample) {
      const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const f = cache[sample] || (cache[sample] = parse(sample));
      const cents = storeCents * rate;
      return f.decimals ? Math.round(cents) : Math.round(cents / 100) * 100;
    },
  };
})();

const cdhMoney = window.cdhMoney;

/* Progress bar (Honeylove style) and automatic free gifts.
   - One equal part of the bar per goal; each part fills from the previous goal's amount to its own.
   - Amounts are converted to the shopper's currency and rounded up to whole units.
   - Message: before the first goal "You're [amount] away from [goal]"; after that the reached goal's
     own message (or, if chosen in settings, how far to the next goal).
   - Goals with a free gift: the gift is added once the goal is reached and removed if the cart drops
     below it. Gift lines carry the hidden property _free_gift and never count towards the goals. */
if (!customElements.get('cart-goal-bar')) {
  const giftGuard = { busy: false, last: '', at: 0 };

  class CartGoalBar extends HTMLElement {
    connectedCallback() {
      this.update();
      this.syncGifts();
    }

    goals() {
      const sample = this.dataset.moneySample;
      return [...this.querySelectorAll('.cdh-goal')]
        .map((el) => ({
          el,
          amount: Math.ceil(cdhMoney.convert(Number(el.dataset.goalAmount) || 0, sample) / 100) * 100,
          label: el.dataset.goalLabel || '',
          reached: el.dataset.goalReached || '',
          gift: Number(el.dataset.giftVariant) || 0,
        }))
        .filter((goal) => goal.amount > 0)
        .sort((a, b) => a.amount - b.amount);
    }

    update() {
      const sample = this.dataset.moneySample;
      const total = Number(this.dataset.total) || 0;
      const goals = this.goals();
      if (!goals.length) return;

      goals.forEach((goal, index) => {
        const previous = index ? goals[index - 1].amount : 0;
        let percent = 0;
        if (total >= goal.amount) percent = 100;
        else if (total > previous) percent = Math.ceil(((total - previous) / (goal.amount - previous)) * 100);
        goal.el.style.order = index;
        goal.el.classList.toggle('is-first', index === 0);
        goal.el.classList.toggle('is-reached', total >= goal.amount);
        goal.el.setAttribute('aria-valuenow', Math.min(100, Math.round((total / goal.amount) * 100)));
        const fill = goal.el.querySelector('[data-goal-fill]');
        if (fill) fill.style.width = `${percent}%`;
        const pill = goal.el.querySelector('[data-goal-value]');
        if (pill) pill.textContent = cdhMoney.format(goal.amount, sample);
      });

      const status = this.querySelector('[data-goal-status]');
      if (!status) return;
      const reached = goals.filter((goal) => total >= goal.amount);
      const highest = reached[reached.length - 1];
      const next = goals.find((goal) => total < goal.amount);
      const away = (goal) =>
        (this.dataset.msgProgress || "You're [amount] away from [goal]")
          .replace(/\[amount\]/g, cdhMoney.format(Math.ceil((goal.amount - total) / 100) * 100, sample))
          .replace(/\[goal\]/g, (goal.label || 'your reward').toLowerCase());
      if (!highest) status.textContent = away(goals[0]);
      else if (this.dataset.between === 'next' && next) status.textContent = away(next);
      else status.textContent = highest.reached || `You've unlocked ${(highest.label || 'your reward').toLowerCase()}`;
    }

    syncGifts() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || giftGuard.busy) return;
      let lines;
      try {
        lines = JSON.parse(this.dataset.giftLines || '[]');
      } catch (e) {
        return;
      }
      const total = Number(this.dataset.total) || 0;
      const goals = this.goals();
      const giftVariants = new Set(goals.filter((goal) => goal.gift).map((goal) => goal.gift));
      const updates = {};
      const adds = [];

      goals
        .filter((goal) => goal.gift)
        .forEach((goal) => {
          const inCart = lines.filter((line) => line.variant === goal.gift);
          const quantity = inCart.reduce((sum, line) => sum + line.quantity, 0);
          if (total >= goal.amount) {
            if (!quantity) adds.push({ id: goal.gift, quantity: 1, properties: { _free_gift: goal.label || 'Free gift' } });
            else if (quantity > 1) inCart.forEach((line, i) => (updates[line.key] = i === 0 ? 1 : 0));
          } else {
            inCart.forEach((line) => (updates[line.key] = 0));
          }
        });
      // A gift that no goal hands out any more (setting changed) leaves the cart too.
      lines.forEach((line) => {
        if (!giftVariants.has(line.variant)) updates[line.key] = 0;
      });
      if (!adds.length && !Object.keys(updates).length) return;

      // Never repeat the exact same change within a few seconds: protects against add/remove loops.
      const signature = JSON.stringify([adds, updates]);
      if (signature === giftGuard.last && Date.now() - giftGuard.at < 5000) return;
      giftGuard.last = signature;
      giftGuard.at = Date.now();
      giftGuard.busy = true;

      const routes = window.routes || {};
      const sections = drawer.getSectionsToRender().map((section) => section.id);
      const post = (url, body) =>
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
        }).then((response) => response.json());

      let chain = Promise.resolve(null);
      if (Object.keys(updates).length) chain = chain.then(() => post(`${routes.cart_update_url || '/cart/update'}.js`, { updates }));
      if (adds.length) chain = chain.then(() => post(`${routes.cart_add_url || '/cart/add'}.js`, { items: adds }));
      chain
        .then((state) => {
          giftGuard.busy = false;
          if (state && !state.status && state.sections) this.renderDrawer(drawer, state);
        })
        .catch(() => {
          giftGuard.busy = false;
        });
    }

    // Re-draws the drawer with the new cart. Keeps it closed if it was closed.
    renderDrawer(drawer, state) {
      if (typeof state.item_count === 'number') drawer.classList.toggle('is-empty', state.item_count === 0);
      if (drawer.classList.contains('active')) {
        drawer.renderContents(state);
        return;
      }
      drawer.getSectionsToRender().forEach((section) => {
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const html = state.sections[section.id];
        if (target && html) target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
      });
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    }
  }
  customElements.define('cart-goal-bar', CartGoalBar);
}

/* Quantity: the minus button stops at 1 (min="1"); Remove takes an item out. Typing 0 removes it too,
   instead of Dawn's "minimum quantity" error. */
if (!window.cdhZeroRemove) {
  window.cdhZeroRemove = true;
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target && event.target.closest && event.target.closest('.cdh .quantity__input');
      if (!input || input.value.trim() !== '0') return;
      event.stopImmediatePropagation();
      const remove = document.querySelector(`#CartDrawer-Remove-${input.dataset.index} button`);
      if (remove) remove.click();
      else input.value = input.getAttribute('value');
    },
    true
  );
}

/* Product offers: sideways slider with arrows, variant dropdowns and one-tap add. */
if (!customElements.get('cart-offers')) {
  class CartOffers extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-offers-track]');
      this.arrows = this.querySelector('[data-offers-arrows]');
      this.prev = this.querySelector('[data-offers-prev]');
      this.next = this.querySelector('[data-offers-next]');

      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-offer-add]');
        if (add) return this.add(add);
        if (event.target.closest('[data-offers-prev]')) return this.slide(-1);
        if (event.target.closest('[data-offers-next]')) return this.slide(1);
      });
      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-offer-select]');
        if (select) this.chooseVariant(select);
      });
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }
      this.updateArrows();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    slide(direction) {
      const card = this.track.querySelector('[data-offer]');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.track || !this.arrows) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.arrows.hidden = max <= 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }

    chooseVariant(select) {
      const card = select.closest('[data-offer]');
      const option = select.selectedOptions[0];
      if (!card || !option) return;
      card.querySelector('[data-offer-add]').dataset.variantId = option.value;
      const price = card.querySelector('[data-offer-price]');
      const compare = card.querySelector('[data-offer-compare]');
      const badge = card.querySelector('[data-offer-badge]');
      const pct = Number(option.dataset.pct) || 0;
      if (price) price.textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !pct;
      }
      if (badge) {
        badge.querySelector('[data-offer-pct]').textContent = pct;
        badge.hidden = !pct;
      }
      const image = card.querySelector('[data-offer-image]');
      if (image && option.dataset.image) image.src = option.dataset.image;
    }

    add(button) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || button.getAttribute('aria-busy') === 'true') return;
      const card = button.closest('[data-offer]');
      const error = card && card.querySelector('[data-offer-error]');
      if (error) error.hidden = true;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      const routes = window.routes || {};
      fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: drawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          // Re-renders the whole drawer (items, total, progress bar, remaining offers).
          drawer.renderContents(state);
        })
        .catch((e) => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          if (error) {
            error.textContent = (e && e.message) || (window.cartStrings && window.cartStrings.error) || 'Could not add this item.';
            error.hidden = false;
          }
        });
    }
  }
  customElements.define('cart-offers', CartOffers);
}

/* Item count next to the title. The header is not part of what Dawn refreshes after a quantity change,
   so a hidden <cdh-cart-count> inside the refreshed area copies the new count up each time it is drawn. */
if (!customElements.get('cdh-cart-count')) {
  customElements.define(
    'cdh-cart-count',
    class extends HTMLElement {
      connectedCallback() {
        const target = document.querySelector('[data-cdh-count]');
        if (target && target.textContent !== this.dataset.count) target.textContent = this.dataset.count;
      }
    }
  );
}

/* Theme editor: keep the drawer open so setting changes can be seen straight away. */
if (window.Shopify && window.Shopify.designMode) {
  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = document.querySelector('cart-drawer[data-editor-open]');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
}
